import { DateTime } from "luxon";
import { HRIS_SOURCES } from "../pages/Settings/DepartmentConfiguration/forms/generalMetadataConsts";
import { EffectiveEmployeeStatus, type HrisRawEmployeeRow, type Nurse } from "./models";

/** Display name for a raw HRIS employee: the provider's full name when present, else first + last. */
export function hrisEmployeeName(emp: Pick<HrisRawEmployeeRow, "fullName" | "firstName" | "lastName">): string {
	return (emp.fullName ?? `${emp.firstName} ${emp.lastName}`).trim();
}

/**
 * The one place a person's full name is assembled for display. `lastNameFirst` is the department's
 * `name_format_last_first` flag — read it with `useLastNameFirst()` (or use `getNurseFormattedName` from
 * `useHospitalData()` / `useFormatPersonName()`), never hand-build `${firstName} ${lastName}` in UI code.
 */
export const formatPersonFullName = (person: { firstName: string; lastName: string }, lastNameFirst: boolean): string =>
	(lastNameFirst ? `${person.lastName} ${person.firstName}` : `${person.firstName} ${person.lastName}`).trim();

// DEFAULT: an empty name part has no initial, so it adds nothing (no stray ".").
const initialOf = (namePart: string): string => (namePart ? `${namePart.charAt(0).toUpperCase()}.` : "");

/**
 * Abbreviated name ("Jane D." / with the flag on "Doe J."), built from the parts rather than by splitting the
 * full name, so multi-word names keep their whole leading part ("Mary Ann S.", "De La Cruz A.").
 */
export const formatNurseName = (nurse: { firstName: string; lastName: string }, lastNameFirst: boolean): string =>
	(lastNameFirst ? `${nurse.lastName} ${initialOf(nurse.firstName)}` : `${nurse.firstName} ${initialOf(nurse.lastName)}`).trim();

/**
 * Whether this nurse can be sent an SMS at all.
 *
 * The broadcast composer will not select someone without BOTH a `userId` and a `phoneNumber`
 * (`useBroadcastMessageState.ts` — its recipient list and its pre-selection both apply this), so any
 * surface offering a "message this person" action has to apply the same rule or it offers a dead end:
 * the composer opens with 0 recipients and a disabled Send.
 *
 * The same expression is inlined in several older call sites (the staff tables, the chat thread list).
 * They are left alone here; point them at this when each is next touched.
 */
export const canReceiveSms = (nurse: Nurse | undefined): boolean => !!nurse?.userId && !!nurse?.phoneNumber;

/** Check if a nurse is active in a specific department based on departmentStatuses. */
export const isNurseActiveInDepartment = (nurse: Pick<Nurse, "departmentStatuses">, departmentId: string): boolean => {
	if (nurse.departmentStatuses && departmentId in nurse.departmentStatuses) {
		return nurse.departmentStatuses[departmentId];
	}
	return false;
};

/** Check if a nurse is active in ANY department. */
export const isNurseActiveAnywhere = (nurse: Nurse): boolean => {
	if (nurse.departmentStatuses) {
		return Object.values(nurse.departmentStatuses).some(Boolean);
	}
	return false;
};

/**
 * The single source of truth for a nurse's effective per-department status on the frontend.
 *
 * Prefers the backend-computed `departmentEmployeeStatuses` map (already resolves the
 * `[start, end)` window + the `active` precedence). Falls back to the legacy
 * `departmentStatuses` boolean (Active/Inactive) when the department has no status row.
 */
export const getNurseEffectiveStatus = (
	nurse: Pick<Nurse, "departmentEmployeeStatuses" | "departmentStatuses">,
	departmentId: string,
): EffectiveEmployeeStatus => {
	const computed = nurse.departmentEmployeeStatuses?.[departmentId];
	if (computed) {
		return computed;
	}
	return isNurseActiveInDepartment(nurse, departmentId) ? EffectiveEmployeeStatus.Active : EffectiveEmployeeStatus.Inactive;
};

/** True when the nurse is currently On Leave or Suspended in the given department. */
export const isNurseOnLeaveOrSuspended = (nurse: Pick<Nurse, "departmentEmployeeStatuses" | "departmentStatuses">, departmentId: string): boolean => {
	const status = getNurseEffectiveStatus(nurse, departmentId);
	return status === EffectiveEmployeeStatus.OnLeave || status === EffectiveEmployeeStatus.Suspended;
};

/**
 * The nurse's effective status on a specific date, mirroring the backend `derive_effective_status`
 * over the stored `[start, end)` window. Unlike `getNurseEffectiveStatus` (which resolves as of
 * today), this answers "would the nurse be On Leave / Suspended on that shift's date" — needed when
 * adding a nurse to a shift that falls inside a future status window. Inactive precedence is not
 * modeled here (adding an inactive nurse is blocked elsewhere); a missing window resolves to Active.
 */
export const deriveNurseStatusAtDate = (nurse: Nurse, departmentId: string, atDate: DateTime): EffectiveEmployeeStatus => {
	const detail = nurse.departmentStatusDetails?.[departmentId];
	if (!detail || !detail.status || !detail.statusStartDate) {
		return getNurseEffectiveStatus(nurse, departmentId);
	}
	const day = atDate.startOf("day");
	const start = DateTime.fromISO(detail.statusStartDate).startOf("day");
	const end = detail.statusEndDate ? DateTime.fromISO(detail.statusEndDate).startOf("day") : null;
	const inWindow = start <= day && (end === null || day < end);
	return inWindow ? detail.status : EffectiveEmployeeStatus.Active;
};

/** True when the nurse can be scheduled in the department right now: active and not On Leave/Suspended. */
export const isNurseSchedulableInDepartment = (nurse: Nurse, departmentId: string): boolean =>
	getNurseEffectiveStatus(nurse, departmentId) === EffectiveEmployeeStatus.Active;

/** The free-text/preset reason behind the current status window, or null when none is set. */
export const getNurseStatusReason = (nurse: Pick<Nurse, "departmentStatusDetails">, departmentId: string): string | null => {
	return nurse.departmentStatusDetails?.[departmentId]?.statusReason ?? null;
};

/** The end date (exclusive) of the current status window as a Luxon DateTime, or null when open-ended/unset. */
export const getNurseStatusEndDate = (nurse: Pick<Nurse, "departmentStatusDetails">, departmentId: string): DateTime | null => {
	const endDateStr = nurse.departmentStatusDetails?.[departmentId]?.statusEndDate;
	return endDateStr ? DateTime.fromISO(endDateStr) : null;
};

/** The start date (inclusive) of the current status window as a Luxon DateTime, or null when unset. */
export const getNurseStatusStartDate = (nurse: Pick<Nurse, "departmentStatusDetails">, departmentId: string): DateTime | null => {
	const startDateStr = nurse.departmentStatusDetails?.[departmentId]?.statusStartDate;
	return startDateStr ? DateTime.fromISO(startDateStr) : null;
};

export type NurseDepartmentStatus = {
	status: EffectiveEmployeeStatus;
	reason: string | null;
	endDate: DateTime | null;
};

/** The nurse's per-department status bundled for EmployeeStatusChip (status + reason + window end). */
/**
 * `external_employee.source` values that represent a payroll/HRIS system, as opposed to an agency
 * feed. Derived from the same list Department Config offers when wiring an HRIS up, so the two cannot
 * disagree about what counts as an HRIS.
 *
 * Agency sources (`clipboard`, `nursa`) are deliberately out: an agency clinician id is a different
 * identifier in a different system, and surfacing it in a column labelled "Employee ID" would have an
 * auditor reconcile it against a payroll export it was never in. `demo` IS in the list — demo
 * departments run the real product surface and should show the column.
 */
const HRIS_EMPLOYEE_SOURCES: ReadonlySet<string> = new Set(HRIS_SOURCES.map(({ value }) => String(value)));

/**
 * Every HRIS employee id this nurse is known by, across all departments. For a search box, where
 * matching an id the person carries anywhere is strictly more useful than scoping it — a search must
 * never hide a hit. Use `getNurseEmployeeIds` for anything the roster DISPLAYS.
 */
export const getNurseHrisEmployeeIdsAnyDepartment = (nurse: Nurse): string[] => collectHrisEmployeeIds(nurse, undefined);

/**
 * The employee IDs an HRIS knows this nurse by in the given department — the "EE ID" Ops audits
 * against (APP-271), i.e. `external_employee.id`, not the In-House `nurse.id`.
 *
 * Returns every DISTINCT id rather than picking one. A nurse can carry several `external_employee`
 * rows for the same department: a second HRIS, an agency feed, or a re-sync under a new sync key.
 * That is ~4.6% of nurse+department pairs, and a minority of those genuinely disagree on the id — so
 * choosing a "winner" would quietly show an auditor a number that is not the one they are
 * reconciling.
 *
 * Sorted so the rendered order is stable across refetches, but NOT with a bare `.sort()`: that is
 * codepoint order, which puts every uppercase id ahead of every lowercase one and reads "demo-10,
 * demo-9". Numeric-aware and case-insensitive matches how someone scanning a list expects ids to run.
 */
export const getNurseEmployeeIds = (nurse: Pick<Nurse, "externalEmployees">, departmentId: string): string[] => collectHrisEmployeeIds(nurse, departmentId);

/** Shared body: `departmentId === undefined` means "any department". */
function collectHrisEmployeeIds(nurse: Pick<Nurse, "externalEmployees">, departmentId: string | undefined): string[] {
	const ids = (nurse.externalEmployees ?? [])
		.filter((employee) => (departmentId === undefined || employee.department_id === departmentId) && HRIS_EMPLOYEE_SOURCES.has(String(employee.source)))
		.map((employee) => employee.id?.trim())
		.filter((id): id is string => !!id);
	return Array.from(new Set(ids)).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }));
}

export const getNurseDepartmentStatus = (
	nurse: Pick<Nurse, "departmentEmployeeStatuses" | "departmentStatuses" | "departmentStatusDetails">,
	departmentId: string,
): NurseDepartmentStatus => ({
	status: getNurseEffectiveStatus(nurse, departmentId),
	reason: getNurseStatusReason(nurse, departmentId),
	endDate: getNurseStatusEndDate(nurse, departmentId),
});
